import React, { useState, useEffect } from "react";
import { BASE_URL } from "./configUrls";
import MOUDetailsModal from "./MOUDetailsModal";
import axios from "axios";

// Circular Progress Component
const CircularProgress = ({ percentage, size = 80, strokeWidth = 6 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#3B82F6"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-300"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-semibold text-gray-700">
          {percentage}%
        </span>
      </div>
    </div>
  );
};

// Feature Details Modal
const FeatureDetailsModal = ({ isOpen, onClose, membership }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {membership.membership_name}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {membership.renewal_label}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="p-6 overflow-auto max-h-[calc(90vh-140px)]">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Feature Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Program and Activities
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {membership.features.map((feature, featureIdx) => {
                  let displayValue = "";

                  if (feature.type === "Count") {
                    displayValue =
                      feature.value_count !== null
                        ? `${feature.value_count}`
                        : "-";
                  } else {
                    // Merge symbol and text for Text type
                    const symbol = feature.symbol ? `${feature.symbol} ` : "";
                    const text = feature.value_text || "";
                    displayValue = symbol + text || "-";
                  }

                  return (
                    <tr key={featureIdx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {feature.feature_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {displayValue}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// History Modal Component
const MembershipHistoryModal = ({ isOpen, onClose, historyData }) => {
  const [selectedMembership, setSelectedMembership] = useState(null);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998] p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">
              Membership History
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          <div className="p-6 overflow-auto max-h-[calc(90vh-80px)]">
            {historyData && historyData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border">
                        Membership Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border">
                        Signup
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border">
                        Renewal
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border">
                        Valid Till
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase border">
                        Invoice No
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase border">
                        Status
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase border">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {historyData.map((membership, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 border">
                          <div className="font-medium">
                            {membership.membership_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {membership.renewal_label}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 border">
                          {membership.signup_display}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 border">
                          {membership.renewal_display || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 border">
                          {membership.valid_till_display}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 border">
                          {membership.invoice_no || "-"}
                        </td>
                        <td className="px-4 py-3 text-center border">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium inline-block ${membership.status === "Current"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                              }`}
                          >
                            {membership.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center border">
                          <button
                            onClick={() => setSelectedMembership(membership)}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                No membership history available
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Feature Details Modal */}
      {selectedMembership && (
        <FeatureDetailsModal
          isOpen={!!selectedMembership}
          onClose={() => setSelectedMembership(null)}
          membership={selectedMembership}
        />
      )}
    </>
  );
};

// Skeleton Loading Component
const SkeletonLoader = () => {
  return (
    <div className="min-h-screen bg-gray-50 p-8 animate-pulse">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="h-8 bg-gray-300 rounded w-64"></div>
          <div className="h-10 bg-gray-300 rounded w-32"></div>
        </div>

        {/* Plan Details Skeleton */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="h-4 bg-gray-300 rounded w-32 mb-4"></div>
          <div className="bg-gray-200 rounded-lg p-4 w-fit">
            <div className="flex items-center space-x-3">
              <div className="bg-gray-300 w-12 h-12 rounded-lg"></div>
              <div>
                <div className="h-3 bg-gray-300 rounded w-20 mb-2"></div>
                <div className="h-6 bg-gray-300 rounded w-48"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Usage Stats Skeleton */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="h-4 bg-gray-300 rounded w-32 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="w-[70px] h-[70px] bg-gray-300 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Programs Skeleton */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="h-4 bg-gray-300 rounded w-48 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SubscriptionDetails() {
  const [membershipData, setMembershipData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showMOUDetails, setShowMOUDetails] = useState(false);
  const [mouPermissions, setMouPermissions] = useState({
    can_view_details: false,
    can_download: false,
  });
  const [downloadingMOU, setDownloadingMOU] = useState(false);

  useEffect(() => {
    const fetchMembershipDetails = async () => {
      try {
        setLoading(true);
        const accessToken = localStorage.getItem("accessToken");

        const response = await axios.get(
          `${BASE_URL}/accounts/my-institute/membership-details`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        setMembershipData(response.data);

        // Fetch MOU permissions
        const permissionsResponse = await axios.get(
          `${BASE_URL}/accounts/spoc/mou-permissions`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        setMouPermissions(permissionsResponse.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMembershipDetails();
  }, []);

  const handleDownloadMOU = async () => {
    if (!membershipData?.current_membership?.mou_id) {
      alert("MOU ID not available");
      return;
    }

    try {
      setDownloadingMOU(true);
      const accessToken = localStorage.getItem("accessToken");

      const response = await axios.get(
        `${BASE_URL}/accounts/spoc/download-mou/${membershipData.current_membership.mou_id}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Convert base64 to blob and download
      const base64Data = response.data.pdf_base64;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = `MOU_${membershipData.current_membership.mou_id}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading MOU:", err);
      alert(err?.response?.data?.detail || "Failed to download MOU");
    } finally {
      setDownloadingMOU(false);
    }
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!membershipData || !membershipData.current_membership || mouPermissions.can_download === undefined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">No membership data available</p>
      </div>
    );
  }

  const { current_membership, membership_history } = membershipData;

  // Filter features by type
  const countFeatures = current_membership.features.filter(
    (feature) => feature.type === "Count"
  );

  const textFeatures = current_membership.features.filter(
    (feature) => feature.type === "Text"
  );

  // Get unique symbols from current features
  const uniqueSymbols = [
    ...new Set(
      current_membership.features
        .map((f) => f.symbol)
        .filter((s) => s && s.trim() !== "")
    ),
  ];

  // Determine membership status
  const currentStatus =
    membership_history && membership_history.length > 0
      ? membership_history[0].status
      : "Current";

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">
            Subscription
          </h1>
          <div className="flex gap-3">
            {/* {mouPermissions.can_view_details && (
              <button
                onClick={() => setShowMOUDetails(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                MOU Details
              </button>
            )} */}
            {mouPermissions.can_download && (
              <button
                onClick={handleDownloadMOU}
                disabled={downloadingMOU}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingMOU ? "Downloading..." : "Download MOU"}
              </button>
            )}
            {/* <button
              onClick={() => setShowHistory(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              View History
            </button> */}
          </div>
        </div>

        {/* Plan Details */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-4">
            Plan Details
          </h2>

          <div className="flex items-center space-x-3 bg-blue-50 rounded-lg p-4 w-fit">
            <div className="bg-blue-600 p-3 rounded-lg flex-shrink-0">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM6 10h2v2H6zm0 4h8v2H6zm10 0h2v2h-2zm-6-4h8v2h-4z" />
              </svg>
            </div>
            <div>
              {/* Plan name + status inline */}
              <div className="text-xs text-gray-500 uppercase mb-0.5">Plan Name</div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-xl font-bold text-gray-900">
                  {current_membership.membership_name}
                </div>
                <span
                  className={`px-3 py-0.5 rounded-full text-xs font-semibold ${currentStatus === "Current" || currentStatus === "Active"
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-600"
                    }`}
                >
                  {currentStatus}
                </span>
              </div>

              {/* Extra details row */}
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
                {current_membership.membership_id && (
                  <div className="text-sm text-gray-500">
                    <span className="font-medium text-gray-600">ID:</span>{" "}
                    {current_membership.membership_id}
                  </div>
                )}
                {current_membership.valid_from_display && (
                  <div className="text-sm text-gray-500">
                    <span className="font-medium text-gray-600">Valid From:</span>{" "}
                    {current_membership.valid_from_display}
                  </div>
                )}
                {current_membership.valid_till_display && (
                  <div className="text-sm text-gray-500">
                    <span className="font-medium text-gray-600">Valid Till:</span>{" "}
                    {current_membership.valid_till_display}
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>

        {/* Usage Stats */}
        {/* {countFeatures.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-6">
              Usage Stats
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {countFeatures.map((feature, index) => {
                const percentage =
                  feature.value_count > 0
                    ? ((feature.used_count || 0) / feature.value_count) * 100
                    : 0;

                return (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <CircularProgress
                        percentage={Math.round(percentage)}
                        size={70}
                      />
                    </div>

                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        {feature.feature_name}
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="font-semibold text-gray-900">
                          {feature.used_count || 0}
                        </span>
                        <span className="text-gray-400">
                          {" "}
                          / {feature.value_count}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )} */}

        {/* Programs / Activities */}
        {textFeatures.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-6">
              Programs / Activities
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {textFeatures.map((feature, index) => {
                // Resolve which used_counts key applies to this feature
                const featureName = feature.feature_name?.trim() || "";
                const usedCounts = current_membership.used_counts || {};

                let usedValue = null;

                if (
                  featureName === "Educators to be trained" ||
                  featureName === "Educators Training Limit" ||
                  featureName === ""
                ) {
                  usedValue =
                    usedCounts.faculty_used !== undefined
                      ? usedCounts.faculty_used
                      : null;
                } else if (
                  featureName === "Student TECH CAMP" ||
                  featureName === "TECH CAMP for Students"
                ) {
                  usedValue =
                    usedCounts.tech_camp_used !== undefined
                      ? usedCounts.tech_camp_used
                      : null;
                } else if (
                  featureName === "Student Limit" ||
                  featureName === "Student Count"
                ) {
                  usedValue =
                    usedCounts.students_limit_used !== undefined
                      ? usedCounts.students_limit_used
                      : null;
                }

                return (
                  <div
                    key={index}
                    className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {feature.feature_name}
                    </span>
                    <div className="flex flex-col items-end gap-0.5">
                      {feature.symbol ? (
                        <span className="text-xl">{feature.symbol}</span>
                      ) : (
                        feature.value_text && (
                          <span className="text-sm text-gray-600">
                            {feature.value_text}
                          </span>
                        )
                      )}
                      {usedValue !== null && (
                        <span className="text-xs text-blue-600 font-medium">
                          used : {usedValue}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Student Limit — Count type shown separately */}
              {(() => {
                const studentLimitFeature = current_membership.features.find(
                  (f) =>
                    f.type === "Count" &&
                    (f.feature_name === "Student Limit" ||
                      f.feature_name === "Student Count")
                );
                const usedCounts = current_membership.used_counts || {};
                const usedValue =
                  usedCounts.students_limit_used !== undefined
                    ? usedCounts.students_limit_used
                    : null;

                if (!studentLimitFeature) return null;

                return (
                  <div
                    key="student-limit"
                    className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {studentLimitFeature.feature_name}
                    </span>
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-sm text-gray-600">
                        {studentLimitFeature.value_count}
                      </span>
                      {usedValue !== null && (
                        <span className="text-xs text-blue-600 font-medium">
                          used : {usedValue}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
              {(() => {
                const educatorTrainingLimitFeature = current_membership.features.find(
                  (f) =>
                    f.type === "Count" &&
                    (f.feature_name === "Educators Training Limit" ||
                      f.feature_name === "Educators to be trained")
                );

                const usedCounts = current_membership.used_counts || {};

                const usedValue =
                  usedCounts.faculty_used !== undefined
                    ? usedCounts.faculty_used
                    : null;

                if (!educatorTrainingLimitFeature) return null;

                return (
                  <div
                    key="educator-training-limit"
                    className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {educatorTrainingLimitFeature.feature_name}
                    </span>

                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-sm text-gray-600">
                        {educatorTrainingLimitFeature.value_count}
                      </span>

                      {usedValue !== null && (
                        <span className="text-xs text-blue-600 font-medium">
                          used : {usedValue}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}


            </div>


            {uniqueSymbols.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex flex-wrap gap-6 text-sm">
                  {uniqueSymbols.map((symbol, idx) => {
                    // Find a feature that uses this symbol to get its name as example
                    const exampleFeature = current_membership.features.find(
                      (f) => f.symbol === symbol
                    );

                    return (
                      <div key={idx} className="flex items-center space-x-2">
                        <span className="text-xl font-semibold">{symbol}</span>
                        <span className="text-gray-600">
                          : {exampleFeature?.value_text || "Feature"}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 text-xs text-gray-500 italic">
                  *T&C Applicable
                  <br />
                  *The Membership Fees are Subject to Change without Prior
                  Notice
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* History Modal */}
      <MembershipHistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        historyData={membership_history}
      />

      {/* MOU Details Modal */}
      {mouPermissions.can_view_details && (
        <MOUDetailsModal
          isOpen={showMOUDetails}
          onClose={() => setShowMOUDetails(false)}
        />
      )}
    </div>
  );
}
